import React, { useState, useRef } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  SelectInput,
  required,
  useRedirect,
  useNotify,
  useTranslate,
  useGetList,
  Toolbar,
  SaveButton,
  DeleteButton,
  useEditContext,
  useInput,
  InputProps,
  FieldTitle,
} from "react-admin";
import { useWatch } from "react-hook-form";
import {
  Typography,
  Alert,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  LinearProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import UploadIcon from "@mui/icons-material/Upload";
import MicIcon from "@mui/icons-material/Mic";
import FirebaseImageInput from "../../components/FirebaseImageInput";
import firebaseDataProvider from "../../providers/dataProvider";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { firebaseApp } from '../../providers/firebase';

const storage = getStorage(firebaseApp);

// Helper to extract YouTube video ID from various URL formats
const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/, // Direct video ID
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Convert YouTube live URLs to standard watch URLs
const normalizeYouTubeUrl = (url: string): string => {
  if (!url) return url;
  const liveMatch = url.match(/youtube\.com\/live\/([^&\n?#]+)/);
  if (liveMatch) {
    return `https://www.youtube.com/watch?v=${liveMatch[1]}`;
  }
  return url;
};


// Helper to get media duration from a file
const getMediaDuration = (file: File, type: 'video' | 'audio'): Promise<number> => {
  return new Promise((resolve) => {
    const element = document.createElement(type);
    element.preload = 'metadata';
    element.onloadedmetadata = () => {
      window.URL.revokeObjectURL(element.src);
      resolve(Math.round(element.duration));
    };
    element.onerror = () => {
      resolve(3600);
    };
    element.src = URL.createObjectURL(file);
  });
};

// Custom input component that handles video URL, video upload, and audio upload
interface EpisodeMediaInputProps extends InputProps {
  label?: string;
}

const EpisodeMediaInput: React.FC<EpisodeMediaInputProps> = (props) => {
  const {
    field: { onChange, value },
  } = useInput(props);
  const translate = useTranslate();
  const notify = useNotify();

  // Determine initial mode based on value
  const getInitialMode = (): 'url' | 'upload' | 'audio' | 'audioUrl' => {
    if (!value) return 'url';
    if (value.mediaType === 'audio') {
      // Check if it's an uploaded audio (firebasestorage) or an audio URL
      if (value.sound?.url?.includes('firebasestorage')) return 'audio';
      return 'audioUrl';
    }
    if (value.video?.url?.includes('firebasestorage')) return 'upload';
    return 'url';
  };

  const [mode, setMode] = useState<'url' | 'upload' | 'audio' | 'audioUrl'>(getInitialMode());
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [urlValue, setUrlValue] = useState(value?.video?.url || '');
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState(
    value?.video?.url?.includes('firebasestorage') ? value.video.url : ''
  );
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState(
    value?.sound?.url?.includes('firebasestorage') ? value.sound.url : ''
  );
  const [audioUrlValue, setAudioUrlValue] = useState(
    value?.mediaType === 'audio' && value?.sound?.url && !value.sound.url.includes('firebasestorage') ? value.sound.url : ''
  );
  const videoInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'url' | 'upload' | 'audio' | 'audioUrl' | null) => {
    if (newMode !== null) {
      setMode(newMode);
      // Only clear the local input values for the mode being switched TO
      if (newMode === 'url') {
        setUrlValue('');
      } else if (newMode === 'audioUrl') {
        setAudioUrlValue('');
      }
      // Update the mode in the form so EpisodeDurationInput knows the current mode
      onChange({
        ...value,
        mode: newMode,
      });
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setUrlValue(url);
    if (url) {
      const youtubeId = extractYouTubeId(url);
      onChange({
        mode: 'url',
        mediaType: 'video',
        video: {
          type: 'video',
          createdAt: Date.now(),
          width: -1,
          height: -1,
          duration: 3600,
          url: url,
          ...(youtubeId && { youtubeId }),
        },
        sound: null,
      });
    } else {
      onChange({ mode: 'url', mediaType: 'video', video: null, sound: null });
    }
  };

  const handleAudioUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setAudioUrlValue(url);
    if (url) {
      onChange({
        mode: 'audioUrl',
        mediaType: 'audio',
        sound: {
          type: 'sound',
          createdAt: Date.now(),
          duration: 3600,
          length: 144004079,
          url: url,
        },
        video: null,
      });
    } else {
      onChange({ mode: 'audioUrl', mediaType: 'audio', video: null, sound: null });
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Only allow .mp4 video files
      const isMp4 = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');
      if (!isMp4) {
        notify(translate('showsPage.invalidVideoFormat'), { type: "error" });
        e.target.value = '';
        return;
      }

      setLoading(true);
      setUploadProgress(0);
      // Mark the field as uploading so the form cannot be saved mid-upload
      onChange({ ...(value || {}), mode: 'upload', mediaType: 'video', uploading: true });
      try {
        const duration = await getMediaDuration(file, 'video');
        const storagePath = `public/videos/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(progress);
          },
          (error) => {
            console.error("Error uploading video", error);
            setLoading(false);
            setUploadProgress(0);
            onChange({ ...(value || {}), uploading: false });
            notify(translate('showsPage.uploadError'), { type: "error" });
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadedVideoUrl(url);
            onChange({
              mode: 'upload',
              mediaType: 'video',
              video: {
                type: 'video',
                createdAt: Date.now(),
                width: -1,
                height: -1,
                duration,
                url,
              },
              sound: null,
              duration,
              uploading: false,
            });
            setLoading(false);
            setUploadProgress(100);
          }
        );
      } catch (err) {
        console.error("Error uploading video", err);
        setLoading(false);
        setUploadProgress(0);
        onChange({ ...(value || {}), uploading: false });
        notify(translate('showsPage.uploadError'), { type: "error" });
      }
    }
  };

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLoading(true);
      setUploadProgress(0);
      const file = e.target.files[0];
      // Mark the field as uploading so the form cannot be saved mid-upload
      onChange({ ...(value || {}), mode: 'audio', mediaType: 'audio', uploading: true });
      try {
        const duration = await getMediaDuration(file, 'audio');
        const storagePath = `public/audio/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(progress);
          },
          (error) => {
            console.error("Error uploading audio", error);
            setLoading(false);
            setUploadProgress(0);
            onChange({ ...(value || {}), uploading: false });
            notify(translate('showsPage.uploadError'), { type: "error" });
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadedAudioUrl(url);
            onChange({
              mode: 'audio',
              mediaType: 'audio',
              sound: {
                type: 'sound',
                createdAt: Date.now(),
                duration,
                length: file.size,
                url,
              },
              video: null,
              duration,
              uploading: false,
            });
            setLoading(false);
            setUploadProgress(100);
          }
        );
      } catch (err) {
        console.error("Error uploading audio", err);
        setLoading(false);
        setUploadProgress(0);
        onChange({ ...(value || {}), uploading: false });
        notify(translate('showsPage.uploadError'), { type: "error" });
      }
    }
  };

  return (
    <FormControl fullWidth margin="normal">
      <FieldTitle label={props.label || "Media"} source={props.source} />

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="url">
            <LinkIcon sx={{ mr: 1 }} />
            {translate('showsPage.videoUrl')}
          </ToggleButton>
          <ToggleButton value="upload">
            <UploadIcon sx={{ mr: 1 }} />
            {translate('showsPage.videoUpload')}
          </ToggleButton>
          <ToggleButton value="audioUrl">
            <LinkIcon sx={{ mr: 1 }} />
            {translate('showsPage.audioUrl')}
          </ToggleButton>
          <ToggleButton value="audio">
            <MicIcon sx={{ mr: 1 }} />
            {translate('showsPage.audioUpload')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'url' && (
        <TextField
          value={urlValue}
          onChange={handleUrlChange}
          placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
          helperText={translate('showsPage.videoUrlHelper')}
          fullWidth
          size="small"
        />
      )}

      {mode === 'upload' && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => videoInputRef.current?.click()}
            size="medium"
            startIcon={loading ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={loading}
            sx={{ width: 200 }}
          >
            {loading ? `${translate('showsPage.uploading')} ${uploadProgress}%` : translate('showsPage.uploadVideo')}
          </Button>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,.mp4"
            onChange={handleVideoUpload}
            style={{ display: 'none' }}
          />
          {loading && (
            <Box sx={{ mt: 1, width: '100%' }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
          {uploadedVideoUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="success.main">
                {translate('showsPage.videoUploadSuccess')}
                {value?.duration && ` (Duration: ${Math.floor(value.duration / 60)}m ${value.duration % 60}s)`}
              </Typography>
              <video
                src={uploadedVideoUrl}
                controls
                style={{ marginTop: 10, maxHeight: 200, maxWidth: '100%' }}
              />
            </Box>
          )}
        </Box>
      )}

      {mode === 'audioUrl' && (
        <Box>
          <TextField
            value={audioUrlValue}
            onChange={handleAudioUrlChange}
            placeholder="https://example.com/audio.mp3"
            helperText={translate('showsPage.audioUrlHelper')}
            fullWidth
            size="small"
          />
          {audioUrlValue && (
            <Box sx={{ mt: 2 }}>
              <audio
                src={audioUrlValue}
                controls
                style={{ marginTop: 10, maxWidth: '100%' }}
              />
            </Box>
          )}
        </Box>
      )}

      {mode === 'audio' && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => audioInputRef.current?.click()}
            size="medium"
            startIcon={loading ? <CircularProgress size={16} /> : <MicIcon />}
            disabled={loading}
            sx={{ width: 200 }}
          >
            {loading ? `${translate('showsPage.uploading')} ${uploadProgress}%` : translate('showsPage.uploadAudio')}
          </Button>
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*"
            onChange={handleAudioUpload}
            style={{ display: 'none' }}
          />
          {loading && (
            <Box sx={{ mt: 1, width: '100%' }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
          {uploadedAudioUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="success.main">
                {translate('showsPage.audioUploadSuccess')}
                {value?.duration && ` (Duration: ${Math.floor(value.duration / 60)}m ${value.duration % 60}s)`}
              </Typography>
              <audio
                src={uploadedAudioUrl}
                controls
                style={{ marginTop: 10, maxWidth: '100%' }}
              />
            </Box>
          )}
        </Box>
      )}
    </FormControl>
  );
};

// Custom duration input. Always editable: auto-detection from uploaded
// media is not always accurate, so the user must be able to override it.
interface EpisodeDurationInputProps extends InputProps {
  label?: string;
}

const EpisodeDurationInput: React.FC<EpisodeDurationInputProps> = (props) => {
  const {
    field: { onChange, value, name },
  } = useInput(props);
  const translate = useTranslate();

  // Derive the media field path from the duration field path
  // e.g., "episodes.0.duration" -> "episodes.0.media"
  const mediaPath = name.replace('.duration', '.media');

  // Watch the media field so we can hint that the value was auto-detected.
  const media = useWatch({ name: mediaPath });

  // Upload modes auto-detect a duration; URL modes default to 3600.
  // mode: 'upload' = video upload, 'audio' = audio upload
  const isUploadMode = media?.mode === 'upload' || media?.mode === 'audio';

  return (
    <TextField
      label={props.label || translate('resources.shows.fields.duration')}
      type="number"
      value={value || 3600}
      onChange={(e) => onChange(parseInt(e.target.value) || 3600)}
      helperText={isUploadMode ? translate('showsPage.durationHelper') : ""}
      fullWidth
      size="small"
      sx={{ mt: 1 }}
    />
  );
};

// Custom category input component
interface CategoryInputProps extends InputProps {
  label?: string;
}

interface CategoryOption {
  id: string;
  name: string;
}

const CategoryInput: React.FC<CategoryInputProps> = (props) => {
  const {
    field: { onChange, value },
  } = useInput(props);
  const translate = useTranslate();

  // Fetch available categories
  const { data: categoriesData, isLoading: categoriesLoading } = useGetList('categories', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'priority', order: 'ASC' },
  });

  const categoryOptions: CategoryOption[] = (categoriesData || [])
    .filter((cat: any) => !cat.isPrimary || cat.layoutType === 'video' || cat.layoutType === 'grid')
    .map((cat: any) => ({ id: cat.id, name: cat.name }));

  // Convert value (array of category names) to CategoryOption[]
  const selectedCategories = (value || [])
    .map((name: string) => categoryOptions.find(opt => opt.name === name))
    .filter(Boolean) as CategoryOption[];

  return (
    <FormControl fullWidth margin="normal">
      <Autocomplete
        multiple
        options={categoryOptions}
        getOptionLabel={(option) => option.name}
        value={selectedCategories}
        onChange={(_, newValue) => onChange(newValue.map(cat => cat.name))}
        loading={categoriesLoading}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label || translate('showsPage.categories')}
            placeholder={translate('showsPage.selectCategories')}
            helperText={translate('showsPage.categoriesHelper')}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={option.name}
              {...getTagProps({ index })}
              key={option.id}
            />
          ))
        }
      />
    </FormControl>
  );
};

// Custom toolbar with save and delete buttons
const VideoShowEditToolbar = () => {
  const translate = useTranslate();
  const episodes = useWatch({ name: 'episodes' });
  const isUploading = Array.isArray(episodes) && episodes.some((ep: any) => ep?.media?.uploading);
  return (
    <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
      <SaveButton disabled={isUploading} />
      <DeleteButton
        mutationMode="pessimistic"
        confirmTitle={translate('showsPage.deleteConfirmTitle')}
        confirmContent={translate('showsPage.deleteConfirmContent')}
        redirect="/shows"
      />
    </Toolbar>
  );
};

// Custom form that converts episodes for editing
const CustomEditForm: React.FC<any> = (props) => {
  const { record } = useEditContext();
  const translate = useTranslate();
  if (!record) return <div>{translate('pos.dashboard.loading')}</div>;

  // Transform episodes for the form
  const formEpisodes = (record.episodes || []).map((ep: any) => {
    // Detect if this is an audio or video episode
    const hasVideo = !!ep.video?.url;
    const isAudioEpisode = !hasVideo;

    // Determine the mode based on media type and URL
    let mode: 'url' | 'upload' | 'audio' | 'audioUrl' = 'url';
    if (isAudioEpisode) {
      // Audio episode - check if it's uploaded or URL
      mode = ep.sound?.url?.includes('firebasestorage') ? 'audio' : 'audioUrl';
    } else {
      // Video episode - check if it's uploaded or URL
      mode = ep.video?.url?.includes('firebasestorage') ? 'upload' : 'url';
    }

    return {
      title: ep.title,
      media: {
        mode,
        mediaType: isAudioEpisode ? 'audio' : 'video',
        video: ep.video || null,
        sound: isAudioEpisode ? ep.sound : null,
        duration: ep.duration || ep.video?.duration || ep.sound?.duration || 3600,
      },
      duration: ep.duration || ep.video?.duration || ep.sound?.duration || 3600,
      description: ep.description || "",
    };
  });

  const computedRecord = {
    ...record,
    actionType: record.actionType || 'disabled',
    episodes: formEpisodes,
    showImage: record.assets?.[0] || null,
    overrideCategories: record.overrideCategories || [],
  };

  return (
    <SimpleForm {...props} record={computedRecord} />
  );
};

const VideoShowEdit: React.FC = () => {
  const redirect = useRedirect();
  const notify = useNotify();
  const translate = useTranslate();

  const transform = (data: any) => {
    const showId = data.id;

    const allEpisodes = data.episodes || [];

    // Block saving while a media upload is still in progress
    if (allEpisodes.some((ep: any) => ep.media?.uploading)) {
      notify(translate('showsPage.uploadInProgress'), { type: "warning" });
      throw new Error('Media upload in progress');
    }

    // Do not silently drop episodes that are missing media: warn and abort instead
    const missingIndex = allEpisodes.findIndex((ep: any) => {
      const media = ep.media || {};
      return !(media.video?.url || media.sound?.url);
    });
    if (missingIndex !== -1) {
      notify(translate('showsPage.episodeMissingMedia', { number: missingIndex + 1 }), { type: "warning" });
      throw new Error('Episode missing media');
    }

    // Transform episodes back to the proper format
    const episodes = allEpisodes.map((ep: any, index: number) => {
      const media = ep.media || {};
      const isAudioEpisode = media.mediaType === 'audio';

      if (isAudioEpisode) {
        // Audio episode - has real sound, no video
        const sound = media.sound || {};
        return {
          id: `ep${showId}${index + 1}`,
          title: ep.title || `Episode ${index + 1}`,
          description: ep.description || "",
          season: new Date().getFullYear().toString(),
          episodeNumber: index + 1,
          duration: ep.duration || sound.duration || 3600,
          length: sound.length || 144004079,
          sound: {
            url: sound.url || "",
            duration: ep.duration || sound.duration || 3600,
            length: sound.length || 144004079,
            type: "sound",
            createdAt: sound.createdAt || Date.now(),
          },
        };
      }

      // Video episode - has placeholder sound and real video
      const video = media.video || {};
      const videoUrl = normalizeYouTubeUrl(video.url || "");
      const youtubeId = video.youtubeId || extractYouTubeId(videoUrl);

      return {
        id: `ep${showId}${index + 1}`,
        title: ep.title || `Episode ${index + 1}`,
        description: ep.description || "",
        season: new Date().getFullYear().toString(),
        episodeNumber: index + 1,
        duration: ep.duration || video.duration || 3600,
        length: 144004079,
        // @deprecated - sound field is no longer used for video episodes but kept for backward compatibility
        sound: {
          url: "https://google.com",
          duration: ep.duration || video.duration || 3600,
          length: 144004079,
          type: "sound",
          createdAt: Date.now(),
        },
        video: {
          type: "video",
          createdAt: video.createdAt || Date.now(),
          width: video.width || -1,
          height: video.height || -1,
          duration: ep.duration || video.duration || 3600,
          url: videoUrl,
          ...(youtubeId && { youtubeId }),
        },
      };
    });

    // Use show image as assets (required field)
    const showAssets = data.showImage?.url ? [data.showImage] : [];

    return {
      ...data,
      titleLowercased: (data.title || "").toLowerCase(),
      searchIndex: (data.title || "").toLowerCase(),
      assets: showAssets,
      episodes,
      episodesCount: episodes.length,
      overrideCategories: data.overrideCategories || [],
    };
  };

  const onSuccess = async (response: any) => {
    try {
      // Update in sync/override/shows collection
      await firebaseDataProvider.set("sync/override/shows", {
        id: response.id,
        data: response,
        previousData: response,
      });
      notify(translate('showsPage.updateSuccess'), { type: "success" });
      redirect("list", "shows");
    } catch (error) {
      console.error("Error updating show:", error);
      notify(translate('showsPage.updateError'), { type: "error" });
    }
  };

  return (
    <Edit
      resource="sync/override/shows"
      transform={transform}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      redirect={false}
      sx={{ maxWidth: 800, mx: "auto" }}
    >
      <CustomEditForm toolbar={<VideoShowEditToolbar />}>
        <Typography variant="h6" gutterBottom>
          {translate('showsPage.editShow')}
        </Typography>

        <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
          {translate('showsPage.editInfoMessage')}
        </Alert>

        <TextInput source="id" label={translate('showsPage.showId')} disabled fullWidth />

        <TextInput
          source="title"
          label={translate('showsPage.showTitle')}
          validate={required()}
          fullWidth
        />

        <TextInput source="subTitle" label={translate('showsPage.subtitle')} fullWidth />

        <TextInput
          source="description"
          label={translate('showsPage.description')}
          multiline
          rows={3}
          fullWidth
        />

        <CategoryInput
          source="overrideCategories"
          label={translate('showsPage.categories')}
        />

        <SelectInput
          source="actionType"
          label="Action Type"
          choices={[
            { id: 'disabled', name: 'Disabled' },
            { id: 'chat', name: 'Chat' },
          ]}
          defaultValue="disabled"
          validate={required()}
          helperText="Controls user interaction: Disabled = no action, Chat = live chat."
          fullWidth
        />

        <FirebaseImageInput
          source="showImage"
          label={translate('showsPage.showImage')}
          validate={required()}
        />

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          {translate('showsPage.episodes')}
        </Typography>

        <ArrayInput source="episodes" label="">
          <SimpleFormIterator sx={{ '& .RaSimpleFormIterator-line': { mb: 3, pb: 3, borderBottom: '1px solid #e0e0e0' } }}>
            <TextInput
              source="title"
              label={translate('showsPage.episodeTitle')}
              validate={required()}
              fullWidth
            />
            <EpisodeMediaInput
              source="media"
              label={translate('showsPage.mediaLabel')}
            />
            <EpisodeDurationInput
              source="duration"
              label={translate('resources.shows.fields.duration')}
            />
            <TextInput
              source="description"
              label={translate('showsPage.episodeDescription')}
              multiline
              rows={2}
              fullWidth
            />
          </SimpleFormIterator>
        </ArrayInput>
      </CustomEditForm>
    </Edit>
  );
};

export default VideoShowEdit;
